import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField } from '@mui/material'
import { AudioObject, AudioObjectInsert, AudioStreamObjectInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
  SubmissionButton,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useAudioObjectCreateMutation } from 'hooks/mutations/content/AudioObject'
import { audioObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects'
import { workspaceQueryKeys } from 'services/queryKeys'

type FormStructure = AudioObjectInsert | AudioStreamObjectInsert

type NewDialogProps = {
  onSuccess?: (data: AudioObject) => void
  initialValue?: AudioObject
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()

  const { workspaceId } = audioObjectsOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const { mutate: createAudioObject, ...audioObjectCreateProps } = useAudioObjectCreateMutation({})

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      repositoryId: workspace?.contentRepositoryId,
      ...initialValue,
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Audio.creatingAudioObject') })

      createAudioObject(
        { audioObjectInsert: data as AudioObjectInsert },
        {
          onSuccess: (data) => {
            setTimeout(() => {
              handleClose()

              if (onSuccess) {
                setTimeout(() => {
                  onSuccess(data)
                }, 300)
              }
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: `${t('domain:Workspace.Audio.somethingWentWrong')}`,
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createAudioObject, handleClose, onSuccess],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Audio.addAudioObject')}
      description={t('domain:Workspace.Audio.audioObjectCreateDescription')}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={audioObjectCreateProps.isSuccess} isError={audioObjectCreateProps.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!audioObjectCreateProps.isSuccess && !audioObjectCreateProps.isError}
          successCondition={audioObjectCreateProps.isSuccess}
          errorCondition={audioObjectCreateProps.isError}
          loading={audioObjectCreateProps.isPending}
          disabled={audioObjectCreateProps.isPending || audioObjectCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="name"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
